import React, { useState } from 'react';
import { FileText, Image as ImageIcon, Paperclip, Download, Trash2, Loader2, MessageCircle } from 'lucide-react';

const iconFor = (mime) => {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return { icon: ImageIcon, color: 'text-sky-600' };
  if (m.includes('pdf')) return { icon: FileText, color: 'text-red-600' };
  if (m.includes('sheet') || m.includes('excel') || m.includes('csv')) return { icon: FileText, color: 'text-emerald-600' };
  if (m.includes('word') || m.includes('document')) return { icon: FileText, color: 'text-blue-600' };
  return { icon: Paperclip, color: 'text-slate-500' };
};

const fmtSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Paleta por tipo de documento — cores discretas. Fallback indigo para tipos fora do mapa.
const DOC_TYPE_COLORS = {
  proposal:        'bg-purple-100 text-purple-700',
  contract:        'bg-slate-800 text-white',
  due_diligence:   'bg-amber-100 text-amber-800',
  appraisal:       'bg-emerald-100 text-emerald-800',
  site_photo:      'bg-sky-100 text-sky-800',
  land_title:      'bg-rose-100 text-rose-800',
  feasibility:     'bg-blue-100 text-blue-800',
  presentation:    'bg-pink-100 text-pink-800',
  meeting_notes:   'bg-stone-100 text-stone-800',
  correspondence:  'bg-cyan-100 text-cyan-800',
  financial_model: 'bg-green-100 text-green-800',
  other:           'bg-slate-100 text-slate-700',
};

/**
 * Mini-card de anexo: badge do tipo + icone mime + filename + tamanho +
 * 💬 tooltip da descricao + download + delete.
 *
 * Props adicionais:
 *   tiposMap: Map<value, label_pt> para exibir label do tipo em pt-BR.
 */
const AttachmentBadge = ({ attachment, onDownload, onDelete, compact = false, tiposMap }) => {
  const [busy, setBusy] = useState(false);
  const { icon: Icon, color } = iconFor(attachment.mime_type);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!onDownload) return;
    setBusy(true);
    try { await onDownload(attachment); }
    finally { setBusy(false); }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!onDelete) return;
    if (!window.confirm(`Remover anexo "${attachment.filename}"?`)) return;
    setBusy(true);
    try { await onDelete(attachment.id); }
    finally { setBusy(false); }
  };

  const docTypeLabel = attachment.document_type
    ? (tiposMap?.get?.(attachment.document_type) || attachment.document_type)
    : null;
  const docTypeColor = DOC_TYPE_COLORS[attachment.document_type] || 'bg-indigo-100 text-indigo-700';

  // Tooltip rico: tipo + descricao + origem + autor + tamanho
  const tooltipLines = [
    `${attachment.filename} (${fmtSize(attachment.size_bytes)})`,
    docTypeLabel ? `Tipo: ${docTypeLabel}` : null,
    attachment.description ? `Descrição: ${attachment.description}` : null,
    attachment.source ? `Origem: ${attachment.source}` : null,
    attachment.uploaded_by ? `Por: ${attachment.uploaded_by}` : null,
  ].filter(Boolean).join('\n');

  return (
    <div
      className={`group inline-flex items-center gap-1.5 ${compact ? 'px-1.5 py-0.5' : 'px-2 py-1'} bg-white border border-slate-200 rounded text-xs hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors`}
      title={tooltipLines}
    >
      {docTypeLabel && (
        <span className={`${docTypeColor} px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0`}>
          {docTypeLabel}
        </span>
      )}
      <Icon size={compact ? 11 : 13} className={`${color} flex-shrink-0`} />
      <button
        onClick={handleDownload}
        disabled={busy}
        className="text-slate-700 hover:text-indigo-700 truncate max-w-[220px] text-left"
      >
        {attachment.filename}
      </button>
      <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtSize(attachment.size_bytes)}</span>
      {attachment.description && (
        <span
          className="text-slate-400 cursor-help flex-shrink-0"
          title={attachment.description}
        >
          <MessageCircle size={10} />
        </span>
      )}
      {busy ? (
        <Loader2 size={10} className="text-slate-400 animate-spin" />
      ) : (
        <>
          <button
            onClick={handleDownload}
            className="text-slate-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Baixar"
          >
            <Download size={11} />
          </button>
          {onDelete && (
            <button
              onClick={handleDelete}
              className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remover"
            >
              <Trash2 size={11} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AttachmentBadge;
